import React from 'react';
import { Table, Accordion, Badge, Alert } from 'react-bootstrap';
import { PERIODIC_REPORT_TYPES } from '../reportsEnums'; // Make sure the path is correct
import SendStudentReport from './sendReports/sendStudentReport';
/**
 * Helper to display Yes/No/Other status with colored badges
 */
const getStatusBadge = (status) => {
  const statusLower = typeof status === 'string' ? status.toLowerCase() : '';
  if (statusLower === 'yes') {
    return <Badge bg="success">Yes</Badge>;
  }
  if (statusLower === 'no') {
    return <Badge bg="danger">No</Badge>;
  }
  // Handle the '-' case or any other unexpected values
  return <Badge bg="secondary">{status}</Badge>;
};

/**
 * Helper to display mock exam states with colored badges
 * (Expanded version from MockReportView)
 */
const getMockStateBadge = (state) => {
  const stateLower = typeof state === 'string' ? state.toLowerCase() : '';
  switch (stateLower) {
    case 'approved':
      return <Badge bg="success">Approved</Badge>;
    case 'pending':
      return (
        <Badge bg="warning" text="dark">
          Pending
        </Badge>
      );
    case 'submitted':
      return <Badge bg="info">Submitted</Badge>;
    case 'cancelled':
    case 'rejected':
      return <Badge bg="danger">{state}</Badge>; // Capitalized as received
    default:
      return <Badge bg="secondary">{state || 'N/A'}</Badge>;
  }
};


/**
 * Renders the specific view for Periodic Activity reports.
 */
function PeriodicActivityView({ reportData, selectedUsers }) {
  // Use currentClass.studentsIDS for full student details
  const allStudents = reportData?.currentClass?.studentsIDS || [];
  const sessions = reportData?.sessions || []; // Use the top-level sessions array
  const classId = reportData?.currentClass?._id;
  // Filter students based on the selection from the parent component
  const studentList =
    selectedUsers && selectedUsers.length > 0
      ? allStudents.filter((s) => selectedUsers.includes(s._id))
      : allStudents;

  if (!reportData || !reportData.studentMap || studentList.length === 0 || sessions.length === 0) {
    return <Alert variant="info">No periodic activity data found for the selected criteria.</Alert>;
  }

  return (
    <Accordion defaultActiveKey="0">
      {studentList.map((student, index) => {
        const studentId = student._id;

        return (
          <Accordion.Item eventKey={String(index)} key={studentId}>
            <Accordion.Header>{student.name || `Student ID: ${studentId}`}</Accordion.Header>
            <Accordion.Header>
              <SendStudentReport studentId={studentId} classId={classId}/>
            </Accordion.Header>
            <Accordion.Body>

              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Session Name</th>
                    <th>Session Date</th>
                    <th>Attendance</th>
                    <th>Duration (h:mm)</th>
                    <th>Homework</th>
                    <th>HW Deadline</th>
                    
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => {
                    const sessionId = session._id;
                    const mapKey = `${studentId}-${sessionId}`;
                    const activityData = reportData.studentMap[mapKey];

                    // Format dates if needed (example assumes DD/MM/YYYY is fine)
                    const sessionDate = activityData?.sessionDate || new Date(session.date).toLocaleDateString('en-GB'); // Fallback to session date
                    const deadlineDate = activityData?.homeworkDeadline && activityData.homeworkDeadline !== '01/01/1970'
                       ? activityData.homeworkDeadline
                       : '-'; // Show '-' if deadline is epoch or missing

                    return (
                      <tr key={mapKey}>
                        <td>{activityData?.name || session.note}</td>
                        <td>{sessionDate}</td>
                        <td>{getStatusBadge(activityData?.attended ?? 'N/A')}</td>
                        <td>{activityData?.duration ?? '-'}</td>
                        <td>{getStatusBadge(activityData?.homework ?? 'N/A')}</td>
                        <td>{deadlineDate}</td>
                      </tr>
                    );
                  })}
                  {/* Add a message if no sessions match? Optional. */}
                  {sessions.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center">No sessions found in this period.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

/**
 * Renders the specific view for Periodic Mock reports.
 */
function PeriodicMockView({ reportData, selectedUsers }) {
  const allStudents = reportData?.currentClass?.studentsIDS || [];
  const studentMap = reportData?.studentMap || {};

  // 1. Group attempts by student ID
  const groupedAttempts = {};
  for (const key in studentMap) {
    // Safely extract studentId assuming it's before the first '-'
    const studentId = key.substring(0, key.indexOf('-'));
    if (studentId) {
      if (!groupedAttempts[studentId]) {
        groupedAttempts[studentId] = [];
      }
      groupedAttempts[studentId].push(studentMap[key]);
    }
  }

  // 2. Filter students based on selection
  const studentList =
    selectedUsers && selectedUsers.length > 0
      ? allStudents.filter((s) => selectedUsers.includes(s._id))
      : allStudents;

   if (studentList.length === 0 || Object.keys(groupedAttempts).length === 0) {
    return <Alert variant="info">No mock exam data found for the selected students in this period.</Alert>;
  }

  return (
    <Accordion defaultActiveKey="0">
      {studentList.map((student, index) => {
        const studentId = student._id;
        const attempts = groupedAttempts[studentId] || []; // Get attempts for this student

        return (
          <Accordion.Item eventKey={String(index)} key={studentId}>
            <Accordion.Header>{student.name || `Student ID: ${studentId}`}</Accordion.Header>
            <Accordion.Body>
              {attempts.length > 0 ? (
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Mock Name</th>
                      <th>Marks</th>
                      <th>State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempts.map((attempt, idx) => (
                      <tr key={`${studentId}-attempt-${idx}`}>
                        <td>{attempt.mockName}</td>
                        {/* Display marks or '-' if null/undefined */}
                        <td>{attempt.marks ?? '-'}</td>
                        <td>{getMockStateBadge(attempt.state)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p>No mock exam attempts found for this student in the selected period.</p>
              )}
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}


/**
 * Main component to render Periodic reports.
 * Decides which specific view to show based on periodicType.
 */
function PeriodicReportView({ reportData, periodicType, selectedUsers }) {
  switch (periodicType) {
    case PERIODIC_REPORT_TYPES.ACTIVITY:
      return <PeriodicActivityView reportData={reportData} selectedUsers={selectedUsers} />;

    case PERIODIC_REPORT_TYPES.MOCK: // <-- Added Case
      return <PeriodicMockView reportData={reportData} selectedUsers={selectedUsers} />;

    // --- Add cases for other periodic types later ---
    // case PERIODIC_REPORT_TYPES.TEST:
    //   return <PeriodicTestView reportData={reportData} selectedUsers={selectedUsers} />;

    default:
      // Fallback for unimplemented or unknown types
      return (
        <Alert variant="warning">
          The preview for the selected periodic report type ({periodicType || 'None'}) is not implemented yet.
          <hr />
          <pre style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {JSON.stringify(reportData, null, 2)}
          </pre>
        </Alert>
      );
  }
}

export default PeriodicReportView;